import { ResellerSubAccountFeatures, User, UserAccount } from '@/types/TAccounts';
import apiClient from '@/utils/apiClient';
import LocalStorageUtility from '@/utils/localStorage';
import posthog from 'posthog-js';
import { useMutation } from '@tanstack/react-query';
import { USER_TYPES } from 'src/enums/EUser';
import { EBusinessLocations, EBusinessRevenue, ECallVolume } from 'src/enums/ESmbDataCollection';
import momentTimeZone from 'moment-timezone';
import { CSM_MEMBERS } from 'src/constants';
import { ERegistrationStep, ERegistrationStatus } from '../enums/ERegistrationSteps';

const agentPrefix = '/agent/:agentId';
const NOTIFICATION_BASE_URL = import.meta.env.VITE_APP_NOTIFICATION_SERVER_BASE_URL;
const LOGIN_API_PATH = '/api/v2/receptionist-dashboard/login';
const GET_USER_API_PATH = `/api/v2/receptionist-dashboard${agentPrefix}`;
const GET_SUGGESTIONS_API_PATH = '/api/v2/receptionist-dashboard/get-suggestions';
const MARK_RESOLVED_SUGGESTIONS_API_PATH =
	'/api/v2/receptionist-dashboard/mark-resolve-suggestions';
const UPDATE_USER_API_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/`;
const UPDATE_EXTENSION_DIGIT_API_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/extension-digit`;
const GET_ZAPIER_KEY_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/zapier-configuration`;
const GET_STRIPE_CLIENT_SECRET_PATH = '/api/clients/getCustomerSecret';
const GRANDFATHERED_PATH = '/api/clients/price-grandfathered';
const POST_CALL_WEBHOOK_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/post-call-webhook`;
const POST_CALL_MESSAGE_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/post-call-message`;
const UPDATE_USAGE_LIMIT_PATH = '/api/v2/receptionist-dashboard/usage-limit-setting';
const REACTIVATE_CHECKOUT_PATH = '/api/v2/receptionist-dashboard/reactivate/checkout-url';
const GET_BUSINESS_INFO_FROM_URL =
	'/api/v2/receptionist-dashboard/get-business-information-from-url';
const DELETE_PRONUNCIATION_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/pronunciation/delete`;
const GENERATE_PRONUNCIATION_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/pronunciation/generate`;
const SPEAK_PHONETIC_CARTESIA_PATH =
	'/api/v2/receptionist-dashboard/pronunciation/speak-phonetic-cartesia';
const BILLING_PORTAL_PATH = '/api/v2/receptionist-dashboard/customerPortal/client';
const GET_SIGNEDURL_API_PATH = '/api/v2/receptionist-dashboard/generate-presigned';
const WATERMARK_REMOVAL_CHECKOUT_PATH =
	'/api/v2/receptionist-dashboard/text-watermark-removal-payment';
const extendClientTrial = '/api/stripe/extendClientTrial';
const ADD_TO_OUTBOUND_WAITLIST = '/api/v2/receptionist-dashboard/novi-waitlist/new';
const FIND_IN_OUTBOUND_WAITLIST = '/api/v2/receptionist-dashboard/novi-waitlist';

const UPDATE_USER_CAMPAIGN_PORTAL_API_PATH = '/api/v2/receptionist-dashboard/by-number/';
const CLIENT_EXISTS_NO_PASSWORD_API_PATH = 'clientExistsNoPassword';
const CREATE_CAMPAIGN_WITH_CREATE_CLIENT_API_PATH = '/createClientV2';
const CREATE_RECEPTIONIST_CLIENT = '/createClient';
const RECEPTIONIST_V2_CAREATE_CLIENT = '/api/v2/receptionist-dashboard/create-client';
const INTEGRATION_REUQEST_PATH = '/api/integration-request';

const GENERATE_OAUTH_TOKENS_PATH = (provider: string) =>
	`/api/v2/receptionist-dashboard/oauth/tokens/${provider}`;

const API_BASE = '/api/v2/receptionist-dashboard';

export const GENERATE_KNOWLEDGE_BASE_API = `${API_BASE}/generate-knowledge-base`;
export const CRAWL_URL_API = `${API_BASE}/crawl-url`;
export const UPDATE_KNOWLEDGE_BASE_ACTIVATION_API = `${API_BASE}${agentPrefix}/update-knowledge-base-activation`;
export const DELETE_KNOWLEDGE_BASE_API = `${API_BASE}${agentPrefix}/delete-knowledge-base`;
export const MANUAL_SYNC_KNOWLEDGE_BASE_API = `${API_BASE}${agentPrefix}/manual-sync-knowledge-base`;
export const UPDATE_KNOWLEDGE_BASE_CONTENT_API = `${API_BASE}${agentPrefix}/update-content-knowledge-base`;
export const RESELLER_CLIENT_FEATURES = `${API_BASE}/reseller-client-features`;

const CHECK_VOICE_CREDITS = '/api/v2/receptionist-dashboard/check-voice-credits';
const CONFIGURATION_SUGGESTIONS_API = `/api/v2/receptionist-dashboard${agentPrefix}/configuration-suggestions`;
const APPLY_CONFIGURATION_SUGGESTIONS_API = `/api/v2/receptionist-dashboard${agentPrefix}/configuration-suggestions/apply`;

const REGISTER_BUSINESS_API_PATH = '/api/v2/receptionist-dashboard/register-temp-business';

const CHECK_CONFIGURATION_STATUS_PATH = '/api/v2/receptionist-dashboard/configuration-status';
const UPDATE_DASHBOARD_LANGUAGE_PATH = '/api/users/update-dashboard-language';
const UPDATE_NPS_BY_USERNAME_PATH = '/api/users/:username/nps';

const CREATE_USER_API_PATH = '/api/users/register';
const GET_USER_DATA_API_PATH = '/api/users';
const UPDATE_USER_DATA_API_PATH = '/api/users';
const USER_LOGIN_API_PATH = '/api/users/login';
const GET_AGENTS_API_PATH = '/api/agents';
const GET_COUPONS_API_PATH = '/api/users/coupons';
const GET_CREDITS_API_PATH = '/api/users/credits';
const REMOVE_DISCOUNT_API_PATH = '/api/users/remove-discount';
const APPLY_COUPON_PATH = '/api/users/apply-coupon';
const BUY_CREDITS_API_PATH = '/api/users/credits/buy';
const AUTO_RELOAD_CREDITS_API_PATH = '/api/users/credits/auto-reload';
const GET_SUBSCRIPTION_INFORMATION_API_PATH = '/api/users/subscription/information';
const UPDATE_PASSWORD_API_PATH = '/api/users/password';
const PURCHASE_INBOUND_SEAT_API_PATH = '/api/users/subscription/purchase-inbound-agent';
const REMOVE_INBOUND_SEAT_API_PATH = '/api/users/subscription/remove-inbound-agent';
const PURCHASE_OUTBOUND_SEAT_API_PATH = '/api/users/subscription/purchase-outbound-agent';
const REMOVE_OUTBOUND_SEAT_API_PATH = '/api/users/subscription/remove-outbound-agent';
const DELETE_AGENT_API_PATH = '/api/agents/:agentId/delete';
const CANCEL_SUBSCRIPTION_REASON_PATH = '/api/users/cancel-subscription-reason';
const CANCEL_SUBSCRIPTION_PATH = '/api/users/cancel-subscription';
const APPLY_RETENTION_DISCOUNT_PATH = '/api/users/apply-retention-discount';
const RENEW_SUBSCRIPTION_PATH = '/api/users/renew-subscription';
const CLIENT_PORTAL_SESSION_PATH = '/api/users/stripe-customer-portal-session';
const COMPUTE_CUSTOMER_ID_HASH_PATH = '/api/churnkey/compute-customer-id-hash';
const PAUSE_SUBSCRIPTION_PATH = '/api/v2/receptionist-dashboard/pause-subscription';

const UPDATE_KNOWLEDGE_BASE_AND_WORKFLOWS_PATH = `/api/v2/receptionist-dashboard${agentPrefix}/update-knowledge-base-and-workflows`;

const GET_SHAREABLE_CLIENT_DETAILS_PATH = '/api/v1/shareable-details/:username';
const GENERATE_SHARE_DEMO_TOKEN_PATH = '/api/playground/generate-token';
const GET_SHAREABLE_CREDITS_PATH = '/api/v1/shareable-credits/:username';
const NOTIFY_SLACK_SHARE_DEMO_PATH = '/api/v1/share-demo/slack-notify';

const FINALIZE_CLIENT_REGISTRATION_PATH = '/api/v2/receptionist-dashboard/finalize-registration';
const PASSWORD_SUFFIX = '123$';
interface RegistrationData {
	businessName?: string;
	businessType?: string;
	hasWebsite?: string;
	websiteUrl?: string;
}

interface Feature {
	id: string;
	name: string;
	description: string;
	icon: string;
	benefit: string;
	setupDuration: number;
	setupOrder: number;
}

interface RegistrationResponse {
	success: boolean;
	data: {
		businessName: string;
		businessType: string;
		features: Feature[];
		temp_key: string;
		greetingAudio?: string;
	};
	message?: string;
}

interface ConfigurationSuggestionsResponse {
	success: boolean;
	suggestions: {
		message: string;
		suggestions: Array<{
			type: string;
			value: string;
		}>;
	};
	remaining_suggestions: number;
}

interface IntegrationRequestPayload {
	name: string;
	businessType: string;
	businessTypeOther?: string;
	businessName: string;
	revenue: string;
	requestedIntegration: string;
	username: string;
}

export const createClient = async (payload: any): Promise<any> => {
	try {
		const response = await apiClient.request({
			method: 'PUT',
			endPoint: RECEPTIONIST_V2_CAREATE_CLIENT,
			body: payload,
		});

		return response;
	} catch (err) {
		console.log('Error:: something went wrong', err);
		throw err;
	}
};

export const loginApi = async (payload: { username: string; password: string }): Promise<User> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: LOGIN_API_PATH,
		body: payload,
	});

	return response;
};

export const getUser = async (agentId: string): Promise<User> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${GET_USER_API_PATH.replace(':agentId', agentId)}/`,
	});

	return response;
};

export const generateSuggestions = async (): Promise<User> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_SUGGESTIONS_API_PATH,
	});

	return response?.suggestions;
};

export const markSuggestionResolved = async ({
	suggestionId,
}: {
	suggestionId: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: MARK_RESOLVED_SUGGESTIONS_API_PATH,
		body: { suggestionId },
	});

	return response;
};

export const updateUser = async (payload: any): Promise<unknown> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${UPDATE_USER_API_PATH.replace(':agentId', agentId)}`,
		body,
	});

	return response;
};

//TODO: Need to enhance this as per the requirement, for now it is just a basic implementation
export const logout = () => {
	LocalStorageUtility.clearLocalData();
	window.location.reload();
	posthog.reset();
};

export const getZapierKey = async (params: {
	isDebrand: boolean;
	agentId: string;
}): Promise<{
	apiKey: string;
	inviteUrl: string;
	username: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${GET_ZAPIER_KEY_PATH.replace(':agentId', params.agentId)}/${params.isDebrand}`,
	});

	return response;
};

export const updateStepsCompleted = async (payload: {
	stepNumber: number;
	agentId: string;
}): Promise<{ success: boolean; updated_instruction_steps: number[] }> => {
	const { agentId, ...body } = payload;
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `/api/v2/receptionist-dashboard/agent/${agentId}/steps-completed`,
		body,
	});

	return response;
};

export const updateMultipleStepsCompleted = async (payload: {
	steps: any[];
	agentId: string;
}): Promise<{ success: boolean; updated_instruction_steps: number[] }> => {
	const { agentId, ...body } = payload;
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `/api/v2/receptionist-dashboard/agent/${agentId}/multiple-steps-completed`,
		body,
	});

	return response;
};

export const updatePendingTestingStatus = async (payload: {
	pendingTesting: boolean;
	agentId: string;
}): Promise<{ success: boolean; pendingTesting: boolean }> => {
	const { agentId, ...body } = payload;
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: '/api/v2/receptionist-dashboard/agent/:agentId/update-pending-testing-status'.replace(
			':agentId',
			agentId,
		),
		body,
	});

	return response;
};

export const incrementTotalTestCalls = async (
	agentId: string,
): Promise<{ success: boolean; totalTestCalls: number }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: `/api/v2/receptionist-dashboard/agent/${agentId}/increment-test-calls`,
	});

	return response;
};

export const updateExtensionDigit = async (payload: {
	agentId: string;
	extension_digits: { phoneNumber: string; extension: string }[];
}): Promise<unknown> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${UPDATE_EXTENSION_DIGIT_API_PATH.replace(':agentId', agentId)}`,
		body,
	});

	return response;
};

export const updatePassword = async (payload: {
	oldPassword: string;
	newPassword: string;
}): Promise<unknown> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_PASSWORD_API_PATH,
		body: payload,
	});

	return response;
};

export const getStripeClientSecret = async (
	clientType: 'Reseller' | 'Client',
): Promise<{ success: boolean; customerSecret?: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: GET_STRIPE_CLIENT_SECRET_PATH,
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const createClientPortalSession = async (payload: {
	action: 'subscription_update' | 'payment_method_update' | 'subscription_cancel';
	subscriptionType?: 'Inbound' | 'Outbound';
	clientType?: 'Reseller' | 'Client';
}): Promise<{
	status: string;
	redirectPaymentURL?: string;
	message?: string;
	success?: boolean;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CLIENT_PORTAL_SESSION_PATH,
		body: payload,
		clientType: payload.clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const userGrandfathered = async (payload: {
	username: string;
}): Promise<{
	success: boolean;
	data: {
		grandfathered: boolean;
		oldAmount?: number;
		newAmount?: number;
		interval?: string;
		name?: string;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: GRANDFATHERED_PATH,
		body: payload,
	});

	return response;
};

export const updatePostCallWebhook = async (payload: {
	endpoint: string;
	method: 'POST' | 'PUT' | 'PATCH';
	agentId: string;
}): Promise<{
	success: boolean;
	data?: {
		endpoint: string;
		method: 'POST' | 'PUT' | 'PATCH';
	};
	message?: string;
}> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${POST_CALL_WEBHOOK_PATH.replace(':agentId', agentId)}`,
		body,
	});

	return response;
};

export const deletePostCallWebhook = async (
	agentId: string,
): Promise<{
	success: boolean;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: `${POST_CALL_WEBHOOK_PATH.replace(':agentId', agentId)}`,
	});

	return response;
};

export const updatePostCallMessage = async (payload: {
	endpoint: string;
	method: 'POST' | 'PUT' | 'PATCH';
	agentId: string;
}): Promise<{
	success: boolean;
	data?: {
		endpoint: string;
		method: 'POST' | 'PUT' | 'PATCH';
	};
	message?: string;
}> => {
	const { agentId, ...body } = payload;
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${POST_CALL_MESSAGE_PATH.replace(':agentId', agentId)}`,
		body,
	});

	return response;
};

export const deletePostCallMessage = async ({
	agentId,
	index,
}: {
	agentId: string;
	index?: number;
}): Promise<{
	success: boolean;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: `${POST_CALL_MESSAGE_PATH.replace(':agentId', agentId)}${index !== undefined && index !== null ? `?scenarioIndex=${index}` : ''}`,
	});

	return response;
};

export const updateUsageLimit = async (payload: {
	usage_notification_emails: string[];
	max_minutes: number;
}): Promise<unknown> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_USAGE_LIMIT_PATH,
		body: payload,
	});

	return response;
};

export const getReactivateCheckoutURL = async (
	username: string,
): Promise<{ success: boolean; data?: { url: string }; message?: string }> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${REACTIVATE_CHECKOUT_PATH}/${username}`,
	});

	return response;
};

export const cancelSubReason = async (payload: {
	reason: string;
	subscriptionType: 'Inbound' | 'Outbound';
}): Promise<{ success: boolean }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CANCEL_SUBSCRIPTION_REASON_PATH,
		body: payload,
	});

	return response;
};

export const getBusinessInfoFromUrl = async (payload: {
	url: string;
}): Promise<{ success: boolean; businessName: string; businessInfo: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: GET_BUSINESS_INFO_FROM_URL,
		body: payload,
	});

	return response;
};

export const deletePronunciation = async (payload: {
	word: string;
	agentId: string;
}): Promise<unknown> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${DELETE_PRONUNCIATION_PATH.replace(':agentId', agentId)}`,
		body,
	});

	return response;
};

export const generatePronunciation = async (payload: {
	word: string;
	phonetic?: string;
	agentId: string;
}): Promise<{ data?: string; message?: string }> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'POST',
		endPoint: `${GENERATE_PRONUNCIATION_PATH.replace(':agentId', agentId)}`,
		body,
	});

	return response;
};

export const doesUserExist = async (payload: {
	usernameOrEmail: string;
}): Promise<{ userExists: boolean; message: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: '/api/users/user-exists',
		body: payload,
	});

	return response;
};

export const isResetPasswordLinkValid = async (payload: {
	username: string;
	token: string;
}): Promise<{ isValid: boolean }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: '/api/users/reset-password-link-valid',
		body: payload,
	});

	return response;
};

export const resetPassword = async (payload: {
	username: string;
	password: string;
}): Promise<{ success: boolean }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: '/api/users/reset-password',
		body: payload,
	});

	return response;
};

export const getBillingPortal = async (): Promise<{
	success: boolean;
	data?: { redirectionURL: string };
	error?: string;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: BILLING_PORTAL_PATH,
	});

	return response;
};

export const getSubscriptionInformation = async (
	clientType: string = 'Client',
): Promise<{
	success: boolean;
	data?: {
		inboundPlan: string;
		inboundPlanStatus: string;
		outboundPlan: string;
		outboundPlanStatus: string;
		voicePlanStatus: string;
		inboundSubscriptionInterval: string;
		outboundSubscriptionInterval: string;
		inboundSubscriptionPrice: number;
		inboundCurrentPeriodEnd: string;
		inboundAllowExtendTrial: boolean;
		outboundSubscriptionPrice: number;
		outboundCurrentPeriodEnd: string;
		outboundAllowExtendTrial: boolean;
		inboundSubQuantity: number;
		outboundSubQuantity: number;
	};

	// Error response
	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_SUBSCRIPTION_INFORMATION_API_PATH,
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const textWatermarkRemovalCheckout = async (): Promise<{
	success: boolean;
	data?: { url: string };
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: WATERMARK_REMOVAL_CHECKOUT_PATH,
	});

	return response;
};

export const generateSignedUrl = async ({
	bucketType,
	objectKey,
}: {
	bucketType: string;
	objectKey: string;
}): Promise<{ success: boolean; url: string | undefined }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: `${GET_SIGNEDURL_API_PATH}/${bucketType}`,
		body: {
			objectKey,
		},
	});

	return response;
};

export const ClientExistsNoPassword = async (username: string): Promise<unknown> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: CLIENT_EXISTS_NO_PASSWORD_API_PATH,
		body: { username },
	});

	return response;
};

export const createCampaignWithCreateClient = async (payload: {
	username: string;
	campaignName: string;
	greetingPhrase: string;
	variables: any;
}): Promise<unknown> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CREATE_CAMPAIGN_WITH_CREATE_CLIENT_API_PATH,
		body: payload,
	});

	return response;
};

export const updateUserCampaignPortal = async (payload: any): Promise<unknown> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_USER_CAMPAIGN_PORTAL_API_PATH + payload?.username,
		body: payload,
	});

	return response;
};

export const GetResellerBrandingImage = async (payload: {
	key: string;
}): Promise<{ success: boolean; data: { image: string } }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: '/download_reseller_logo',
		body: payload,
	});

	return response;
};

export const generateKnowledgeBase = async (payload: FormData): Promise<unknown> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: GENERATE_KNOWLEDGE_BASE_API,
		body: payload,
		headers: { 'Content-Type': 'multipart/form-data' },
	});

	return response;
};

export const crawlUrl = async (payload: { url: string; depth?: number }): Promise<unknown> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CRAWL_URL_API,
		body: payload,
	});

	return response;
};

export const updateKnowledgeBaseActivation = async (payload: {
	number: string;
	entryIndex: number;
	isActive: boolean;
	agentId: string;
}): Promise<unknown> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_KNOWLEDGE_BASE_ACTIVATION_API.replace(':agentId', agentId),
		body,
	});

	return response;
};

export const deleteKnowledgeBase = async (payload: {
	number: string;
	entryIndex: number;
	agentId: string;
}): Promise<unknown> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: DELETE_KNOWLEDGE_BASE_API.replace(':agentId', agentId),
		body,
	});

	return response;
};

export const manualSyncKnowledgeBase = async (payload: {
	number: string;
	entryIndex: number;
	entry: string;
	agentId: string;
}): Promise<unknown> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'POST',
		endPoint: MANUAL_SYNC_KNOWLEDGE_BASE_API.replace(':agentId', agentId),
		body,
	});

	return response;
};

export const updateKnowledgeBaseContent = async (payload: {
	number: string;
	entryIndex: number;
	content: string;
	syncType?: string;
	agentId: string;
}): Promise<unknown> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'POST',
		endPoint: UPDATE_KNOWLEDGE_BASE_CONTENT_API.replace(':agentId', agentId),
		body,
	});

	return response;
};

export const createReceptionistClient = async (
	bodyObject: any,
): Promise<{
	success: boolean;
	data: any;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CREATE_RECEPTIONIST_CLIENT,
		body: bodyObject,
	});

	return response;
};

export const generateOAuthIntegrationTokens = async (
	code: any,
	provider: string,
): Promise<{
	accessToken: string;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: GENERATE_OAUTH_TOKENS_PATH(provider),
		body: {
			authCode: code,
		},
	});

	return response;
};

export const updateResellerClientFeatures = async (payload: {
	number: string;
	featuresSubAccount?: ResellerSubAccountFeatures;
}): Promise<{
	success: boolean;
	data: any;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: RESELLER_CLIENT_FEATURES,
		body: payload,
	});

	return response;
};

export const applyCouponCode = async (payload: {
	couponCode: string;
	clientType: 'Reseller' | 'Client';
}): Promise<{
	success: boolean;
	message?: string;

	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const { couponCode, clientType } = payload;

	const response = await apiClient.request({
		method: 'POST',
		endPoint: APPLY_COUPON_PATH,
		body: { couponCode },
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const currentSubscriptionInformation = async (
	clientType: 'Reseller' | 'Client' = 'Client',
): Promise<{
	status: string;
	data?: {
		currentStatus: string;
		trialEnd: string;
		allowExtendTrial: boolean;
		currentPeriodEnd: string;
		currentSubscriptionPrice: number;
		currentSubscriptionProduct: string;
		currentSubscriptionInterval: string;
	};
	error?: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_SUBSCRIPTION_INFORMATION_API_PATH,
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const extendClientTrialApi = async (
	subscriptionId: string,
): Promise<{ status: string; data: any; error?: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: extendClientTrial,
		body: {
			subscriptionId,
		},
	});

	return response;
};

export const removeDiscount = async (payload: {
	appliedOn: string;
	clientType: 'Reseller' | 'Client';
}): Promise<{
	success: boolean;
	message?: string;
}> => {
	const { clientType, ...body } = payload;

	const response = await apiClient.request({
		method: 'POST',
		endPoint: REMOVE_DISCOUNT_API_PATH,
		body,
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const addToOutboundWaitlist = async (payload: {
	email: string;
	name?: string;
	company?: string;
	phone?: string;
	isReseller?: boolean;
}): Promise<{
	success: boolean;
	data: {
		newWaitlistEntry: any;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: ADD_TO_OUTBOUND_WAITLIST,
		body: payload,
		clientType: payload.isReseller ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const findInOutboundWaitlist = async (payload: {
	email: string;
	isReseller?: boolean;
}): Promise<{
	success: boolean;
	data: any;
}> => {
	const email = payload.email;

	if (!email) {
		throw new Error('Email is required');
	}

	const encodedEmail = encodeURIComponent(email);

	const response = await apiClient.request({
		method: 'GET',
		endPoint: FIND_IN_OUTBOUND_WAITLIST + '/' + encodedEmail,
		clientType: payload.isReseller ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const registerBusiness = async (
	payload: RegistrationData,
): Promise<RegistrationResponse> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: REGISTER_BUSINESS_API_PATH,
		body: payload,
	});

	return response;
};

export const useRegisterBusiness = ({ handleError }: { handleError?: (error: Error) => void }) => {
	return useMutation<RegistrationResponse, Error, RegistrationData>({
		mutationFn: (data: RegistrationData) => registerBusiness(data),
		onMutate: (variables) => {
			console.info('Mutation starting with variables:', variables);
		},
		onError: handleError,
		onSuccess: (data) => {
			console.info('Registration mutation succeeded:', data);
		},
		onSettled: (data, error) => {
			console.info('Registration mutation settled:', { data, error });
		},
	});
};

export const getConfigurationSuggestions = async (payload: {
	query: string;
	agentId: string;
}): Promise<ConfigurationSuggestionsResponse> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'POST',
		endPoint: CONFIGURATION_SUGGESTIONS_API.replace(':agentId', agentId),
		body,
	});

	return response;
};

export const applyConfigurationSuggestion = async (payload: {
	agentId: string;
	suggestion: { type: string; value: any };
}): Promise<{ success: boolean; message?: string }> => {
	const { agentId, ...body } = payload;

	const response = await apiClient.request({
		method: 'POST',
		endPoint: APPLY_CONFIGURATION_SUGGESTIONS_API.replace(':agentId', agentId),
		body,
	});

	return response;
};

export const speakPhoneticCartesia = async (payload: { text: string; voiceId?: string }) => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: SPEAK_PHONETIC_CARTESIA_PATH,
		body: payload,
		isBlobResponse: true,
		clientType: 'RECEPTIONIST',
	});

	return response;
};

export const checkVoiceCredits = async ({
	agentType,
}: {
	agentType: 'Inbound' | 'Outbound';
}): Promise<{
	success: boolean;
	hasCredits?: boolean;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${CHECK_VOICE_CREDITS}/${agentType}`,
	});

	return response;
};

export const cancelSubscription = async (
	subscriptionType: 'Inbound' | 'Outbound',
): Promise<{ success: boolean; message?: string; subCancelOn?: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CANCEL_SUBSCRIPTION_PATH,
		clientType: 'RECEPTIONIST',
		body: {
			subscriptionType,
		},
	});

	return response;
};

export const pauseSubscription = async (payload: {
	pauseDuration: number;
	reason: string;
	subscriptionType: 'Inbound' | 'Outbound';
}): Promise<{ success: boolean; message?: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: PAUSE_SUBSCRIPTION_PATH,
		clientType: 'RECEPTIONIST',
		body: payload,
	});

	return response;
};

export const applyRetentionDiscount = async (
	subscriptionType: 'Inbound' | 'Outbound',
): Promise<{ success: boolean; message?: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: APPLY_RETENTION_DISCOUNT_PATH,
		clientType: 'RECEPTIONIST',
		body: {
			subscriptionType,
		},
	});

	return response;
};

export const renewSubscription = async (
	subscriptionType: 'Inbound' | 'Outbound',
	clientType: 'Reseller' | 'Client' = 'Client',
): Promise<{ success: boolean; message?: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: RENEW_SUBSCRIPTION_PATH,
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
		body: {
			subscriptionType,
		},
	});

	return response;
};

export const requestIntegration = async (
	payload: IntegrationRequestPayload,
	userType: string,
): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: INTEGRATION_REUQEST_PATH,
		body: payload,
		params: { userType },
	});

	return response;
};

export const checkConfigurationStatus = async (
	tempKey: string,
): Promise<{
	success: boolean;
	data?: {
		status: 'in_progress' | 'completed' | 'failed';
	};
	error?: string;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${CHECK_CONFIGURATION_STATUS_PATH}/${tempKey}`,
	});

	return response;
};

export const setClientDashboardLanguage = async (payload: {
	language: string;
}): Promise<{
	success: boolean;
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_DASHBOARD_LANGUAGE_PATH,
		body: payload,
	});

	return response;
};

//===================================================== NEW USER APIs =========================================================================
export const createUser = async (payload: {
	username: string;
	emailAddress: string;
	password: string;
	type: 'Client' | 'Reseller' | 'SubAccount';
	resellerId?: string;
	trial?: boolean;
	subAccountType?: 'Inbound' | 'Outbound';
	logo?: File;
	businessName?: string;
	businessWebsite?: string;
	businessType?: string;
	callVolume?: ECallVolume;
	businessLocations?: EBusinessLocations;
	businessRevenue?: EBusinessRevenue;
	interestedFeatures?: string[];
	phoneNumber?: string;
	csm?: string;
	referralSource?: string;
}): Promise<{
	success: boolean;
	// Success response
	data?: UserAccount;
	token?: string;

	// Error response
	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const formData = new FormData();

	Object.entries(payload).forEach(([key, value]) => {
		if (value instanceof File) {
			formData.append(key, value);
		} else if (value !== null && value !== undefined) {
			formData.append(key, value as string);
		}
	});

	const response = await apiClient.request({
		method: 'POST',
		endPoint: CREATE_USER_API_PATH,
		body: formData,
	});

	return response;
};

export const getUserData = async ({
	from,
}: {
	from: USER_TYPES.CLIENT | USER_TYPES.RESELLER;
}): Promise<{
	success: boolean;
	data?: UserAccount;
	token?: string;

	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: GET_USER_DATA_API_PATH,
		body: {
			from,
		},
	});

	return response;
};

export const userLogin = async (payload: {
	username: string;
	password: string;
	from: USER_TYPES.CLIENT;
}): Promise<{
	success: boolean;
	data?: UserAccount;
	token?: string;

	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: USER_LOGIN_API_PATH,
		body: payload,
	});

	return response;
};

export const getAgents = async ({
	query,
}: {
	query?: 'Inbound' | 'Outbound';
}): Promise<{
	success: boolean;
	data?: User[];
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_AGENTS_API_PATH,
		clientType: 'RECEPTIONIST',
		...(query && { params: { query } }),
	});

	return response;
};

export const getCoupons = async (
	clientType: string = 'Client',
): Promise<{
	success: boolean;
	data?: {
		appliedCoupons:
			| {
					id: string;
					name: string;
					amountOff: number | null;
					percentOff: number | null;
					duration: string;
					durationInMonths: number | null;
					validUntil: string | null;
					startedOn: string | null;
					appliedOn: string;
			  }[]
			| null;
	};
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_COUPONS_API_PATH,
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const getCredits = async (
	userType: 'Client' | 'Reseller',
): Promise<{
	success: boolean;
	data?: {
		voiceTotalCredits: number;
		voiceFreeCredits: number;
		voiceCredits: number;
		voiceThresholdCredits: number;
		voiceCreditsToReload: number;
		voiceAutoReloadCredits: boolean;

		// sms credits
		smsTotalCredits: number;
		smsFreeCredits: number;
		smsCredits: number;
		smsThresholdCredits: number;
		smsCreditsToReload: number;
		smsAutoReloadCredits: boolean;

		// plan information
		planName?: string;
		freeVoiceCredits?: number;
		freeSMSCredits?: number;

		// voice and sms credits expire and refill dates
		freeCreditsExpiresOn?: string;
		freeCreditsRefillsOn?: string;
		freeCreditsLastRefilledOn?: string;
	};
	error?: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_CREDITS_API_PATH,
		clientType: userType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const buyCredits = async (payload: {
	credits: number;
	creditsFor: 'voice' | 'sms';
	userType: 'Client' | 'Reseller';
}): Promise<{
	success: boolean;
	message?: string;
	error?: string;
}> => {
	const { userType, ...body } = payload;

	const response = await apiClient.request({
		method: 'POST',
		endPoint: BUY_CREDITS_API_PATH,
		body,
		clientType: userType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const updateAutoReloadCredits = async (payload: {
	thresholdCredits: number;
	creditsToReload: number;
	autoReloadCredits: boolean;
	settingsFor: 'voice' | 'sms';
	userType: 'Client' | 'Reseller';
}): Promise<{
	success: boolean;
	data?: {
		thresholdCredits: number;
		creditsToReload: number;
		autoReloadCredits: boolean;
	};
	error?: string;
}> => {
	const { userType, ...body } = payload;

	const response = await apiClient.request({
		method: 'POST',
		endPoint: AUTO_RELOAD_CREDITS_API_PATH,
		body,
		clientType: userType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const purchaseInboundSeat = async (
	quantity: number,
): Promise<{
	success: boolean;
	message?: string;

	// Error response
	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: PURCHASE_INBOUND_SEAT_API_PATH,
		body: { quantity },
	});

	return response;
};

export const removeInboundSeat = async (): Promise<{
	success: boolean;
	message?: string;
	// Error response
	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: REMOVE_INBOUND_SEAT_API_PATH,
	});

	return response;
};

export const purchaseOutboundSeat = async (
	quantity: number,
): Promise<{
	success: boolean;
	message?: string;
	// Error response
	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: PURCHASE_OUTBOUND_SEAT_API_PATH,
		body: { quantity },
	});

	return response;
};

export const removeOutboundSeat = async (): Promise<{
	success: boolean;
	message?: string;
	// Error response
	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: REMOVE_OUTBOUND_SEAT_API_PATH,
	});

	return response;
};

export const deleteAgent = async ({
	agentId,
	clientType,
}: {
	agentId: string;
	clientType: 'Reseller' | 'Client';
}): Promise<{
	success: boolean;
	message?: string;
	// Error response
	status?: string;
	error?: {
		name?: string;
		message?: string;
		statusCode?: number;
		isOperational?: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: DELETE_AGENT_API_PATH.replace(':agentId', agentId),
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const updateUserData = async ({
	clientType,
	data,
}: {
	clientType: 'Client' | 'Reseller';
	data: any;
}): Promise<{ success: boolean; message?: string }> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_USER_DATA_API_PATH,
		body: data,
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const updateNPSByUsername = async ({
	username,
	nps,
}: {
	username: string;
	nps: number;
}): Promise<{ success: boolean; data?: any; message?: string }> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_NPS_BY_USERNAME_PATH.replace(':username', username),
		body: { nps },
	});

	return response;
};

export const updateAgentKnowledgeBaseAndWorkflows = async (
	agentId: string,
	data: {
		structuredData: {
			businessName?: string;
			phoneNumber?: string;
			addresses?: any[];
			businessHours?: any;
			pricing?: any[];
			faqs?: any[];
			additionalInfo?: string;
		};
		workflows: {
			callWorkflows?: any[];
			intakeWorkflows?: any[];
			textingWorkflows?: any[];
		};
	},
) => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: UPDATE_KNOWLEDGE_BASE_AND_WORKFLOWS_PATH.replace(':agentId', agentId),
		body: data,
	});

	return response.data;
};

export const getShareableClientDetails = async (username: string): Promise<any> => {
	const url = `${NOTIFICATION_BASE_URL}${GET_SHAREABLE_CLIENT_DETAILS_PATH.replace(':username', username)}`;
	const response = await fetch(url, {
		method: 'GET',
		headers: { 'Content-Type': 'application/json' },
	});
	if (response.status === 404) {
		const error = new Error('NotFound');
		(error as any).code = 404;
		throw error;
	}
	if (!response.ok) {
		throw new Error('Failed to fetch client details');
	}
	return response.json();
};

export const generateShareDemoToken = async (
	username: string,
	testingNumber: string,
): Promise<any> => {
	const url = `${import.meta.env.VITE_APP_API_BASE_URL}${GENERATE_SHARE_DEMO_TOKEN_PATH}`;
	const response = await fetch(url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ username, testing_number: testingNumber, is_reseller: false }),
	});
	if (!response.ok) {
		throw new Error('Failed to generate token');
	}
	return response.json();
};

export const getShareableCredits = async (username: string): Promise<any> => {
	const url = `${NOTIFICATION_BASE_URL}${GET_SHAREABLE_CREDITS_PATH.replace(':username', username)}`;
	const response = await fetch(url, {
		method: 'GET',
		headers: { 'Content-Type': 'application/json' },
	});
	if (!response.ok) {
		throw new Error('Failed to fetch credit information');
	}
	return response.json();
};

export const notifySlackShareDemo = async (username: string, source: string) => {
	const url = `${NOTIFICATION_BASE_URL}${NOTIFY_SLACK_SHARE_DEMO_PATH}`;
	const shareSource = source === 'email' ? '✉️' : '🔗';
	await fetch(url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			username,
			message: `${shareSource} Share Call Heard: User tested AI receptionist of (${username})`,
		}),
	});
};

export const computeCustomerIdHash = async (payload: { customerId: string }) => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: COMPUTE_CUSTOMER_ID_HASH_PATH,
		body: payload,
		clientType: 'RECEPTIONIST',
	});
	return response.data;
};

export const affiliateRegistration = async (
	formValues: {
		businessName: string;
		businessType: string;
		otherBusinessType?: string;
		emailAddress: string;
		phoneNumber?: string;
		businessWebsite?: string;
		referralId?: string;
	},
	onProgress?: (step: ERegistrationStep, status: ERegistrationStatus) => void,
): Promise<{
	success: boolean;
	data?: {
		username: string;
		password: string;
		email: string;
		phone: string;
		shareLink: string;
	};
	error?: string;
}> => {
	try {
		onProgress?.(ERegistrationStep.USER, ERegistrationStatus.IN_PROGRESS);
		const username = formValues.businessName.toLowerCase().replace(/[^a-z0-9]/g, '');
		const password = `${username}${PASSWORD_SUFFIX}`;
		const randomIndex = Math.floor(Math.random() * CSM_MEMBERS.length);
		const selectedCsm = CSM_MEMBERS[randomIndex];

		const createUserPayload = {
			username,
			emailAddress: formValues.emailAddress,
			password,
			type: USER_TYPES.CLIENT,
			businessName: formValues.businessName,
			businessWebsite: formValues.businessWebsite || undefined,
			businessType:
				formValues.businessType === 'Other'
					? formValues.otherBusinessType
					: formValues.businessType,
			phoneNumber: formValues.phoneNumber || undefined,
			csm: selectedCsm.email,
			automaticallyRegistered: true,
			referralId: formValues.referralId || undefined,
		};

		const userResponse = await createUser(createUserPayload);
		if (!userResponse.success) {
			const errorMessage =
				userResponse.error?.message || userResponse.status || 'User creation failed';
			onProgress?.(ERegistrationStep.USER, ERegistrationStatus.FAILED);
			throw new Error(errorMessage);
		}

		onProgress?.(ERegistrationStep.USER, ERegistrationStatus.COMPLETED);

		// Step 2: Register business
		onProgress?.(ERegistrationStep.BUSINESS, ERegistrationStatus.IN_PROGRESS);
		const businessData = {
			businessName: formValues.businessName,
			businessType:
				formValues.businessType === 'Other'
					? formValues.otherBusinessType
					: formValues.businessType,
			hasWebsite: formValues.businessWebsite && formValues.businessWebsite.trim() ? 'yes' : 'no',
			websiteUrl:
				formValues.businessWebsite && formValues.businessWebsite.trim()
					? `https://${formValues.businessWebsite.trim()}`
					: '',
		};

		const businessResponse = await registerBusiness(businessData);
		if (!businessResponse.success) {
			const errorMessage = businessResponse.message || 'Business registration failed';
			onProgress?.(ERegistrationStep.BUSINESS, ERegistrationStatus.FAILED);
			throw new Error(errorMessage);
		}
		onProgress?.(ERegistrationStep.BUSINESS, ERegistrationStatus.COMPLETED);

		onProgress?.(ERegistrationStep.CONFIGURING, ERegistrationStatus.IN_PROGRESS);
		const tempKey = businessResponse.data.temp_key;

		const maxAttempts = 12 * 5; // 5 minutes
		let attempts = 0;
		let configurationComplete = false;

		while (attempts < maxAttempts && !configurationComplete) {
			attempts++;

			const statusResponse = await checkConfigurationStatus(tempKey);

			if (statusResponse.data?.status === 'completed') {
				configurationComplete = true;
			} else if (statusResponse.data?.status === 'failed') {
				onProgress?.(ERegistrationStep.CONFIGURING, ERegistrationStatus.FAILED);
				throw new Error('Configuration generation failed');
			} else if (statusResponse.data?.status === 'in_progress') {
				await new Promise((resolve) => setTimeout(resolve, 5000));
			} else {
				await new Promise((resolve) => setTimeout(resolve, 5000));
			}
		}

		if (!configurationComplete) {
			onProgress?.(ERegistrationStep.CONFIGURING, ERegistrationStatus.FAILED);
			throw new Error('Configuration generation timed out after 60 seconds');
		}
		onProgress?.(ERegistrationStep.CONFIGURING, ERegistrationStatus.COMPLETED);

		onProgress?.(ERegistrationStep.FINALIZING, ERegistrationStatus.IN_PROGRESS);
		const finalizePayload = {
			userId: userResponse.data.id,
			agentType: 'Inbound',
			temp_key: tempKey,
			emailAddress: formValues.emailAddress,
			time_zone: momentTimeZone.tz.guess(),
			automaticallyRegistered: true,
			name: 'My First AI Agent',
		};

		const finalizeResponse = await apiClient.request({
			method: 'POST',
			endPoint: FINALIZE_CLIENT_REGISTRATION_PATH,
			body: finalizePayload,
		});

		if (!finalizeResponse.success) {
			onProgress?.(ERegistrationStep.FINALIZING, ERegistrationStatus.FAILED);
			throw new Error(finalizeResponse.message || 'Finalization failed');
		}
		onProgress?.(ERegistrationStep.FINALIZING, ERegistrationStatus.COMPLETED);

		return {
			success: true,
			data: {
				username,
				password,
				email: formValues.emailAddress,
				phone: formValues.phoneNumber || '',
				shareLink: `${import.meta.env.VITE_APP_BASE_URL}/share/${username}?source=email`,
			},
		};
	} catch (error) {
		return {
			success: false,
			error: error instanceof Error ? error.message : 'Registration failed',
		};
	}
};

export const affiliateLogin = async (
	email: string,
): Promise<{
	success: boolean;
	data?: {
		affiliateData: any;
	};
	error?: string;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: '/api/affiliate/affiliate-login',
		body: {
			email,
		},
	});

	return response;
};
export const affiliateDailyCountCheck = async (
	referralId: string[],
): Promise<{
	success: boolean;
	count?: number;
	error?: string;
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: '/api/affiliate/affiliate-daily-count-check',
		body: {
			referralId,
		},
	});

	return response;
};
